import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Table,
  message,
  Typography,
  Spin,
  Button,
  Modal,
  Space,
  Popconfirm,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  Divider,
  Progress,
  Alert,
} from "antd";
import { AppContext } from "../context/AppContext";
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  CloseCircleOutlined,
  ScanOutlined,
  StopOutlined,
  MobileOutlined,
  TagsOutlined,
  PlusOutlined,
  ToolOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  UploadOutlined,
  DownloadOutlined,
  InboxOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Papa from "papaparse";

const { Title, Text } = Typography;

const GpsTrackers = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deactivatingTracker, setDeactivatingTracker] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [loans, setLoans] = useState([]);
  const [addForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [deletingTracker, setDeletingTracker] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
  });
  const token = localStorage.getItem("token");

  const fetchTrackers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/gps_trackers?fields=*,issued_to.id,issued_to.first_name,issued_to.last_name,assigned_loan.id,assigned_loan.borrower.first_name,assigned_loan.borrower.last_name,assigned_loan.amount&filter[company]=${company.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setTrackers(response.data.data);
    } catch (err) {
      console.error("Error fetching GPS trackers:", err);
      setError("Failed to load GPS trackers");
      message.error("Error loading GPS tracker data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLoans = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?fields=id,amount,borrower.first_name,borrower.last_name,borrower.id&filter[company]=${company.id}&filter[loan_status]=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setLoans(response.data.data);
    } catch (err) {
      console.error("Error fetching loans:", err);
      message.error("Error loading loans");
    }
  };

  const handleAddTracker = async (values) => {
    try {
      setSubmitting(true);

      // Check for duplicates
      const existingSerial = trackers.find(
        (t) => t.serial_number === values.serial_number.trim()
      );
      const existingSim = trackers.find(
        (t) => t.sim_number === values.sim_number.trim()
      );

      if (existingSerial) {
        message.error(`Serial number ${values.serial_number} already exists`);
        setSubmitting(false);
        return;
      }

      if (existingSim) {
        message.error(`SIM number ${values.sim_number} already exists`);
        setSubmitting(false);
        return;
      }

      const payload = {
        ...values,
        company: company.id,
        status: "not_assigned",
        date_created: new Date().toISOString(),
      };

      await axios.post(
        "https://zedlenders.pickmesms.com/items/gps_trackers?fields=*.*",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker added successfully");
      setShowAddModal(false);
      addForm.resetFields();
      fetchTrackers();
    } catch (err) {
      console.error("Error adding GPS tracker:", err);
      message.error(
        "Failed to add GPS tracker: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignToLoan = async (values) => {
    try {
      setSubmitting(true);
      const selectedLoan = loans.find((loan) => loan.id === values.loan_id);

      // Update the GPS tracker
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${selectedTracker.id}`,
        {
          status: "active",
          issued_to: selectedLoan.borrower.id,
          assigned_loan: values.loan_id,
          date_issued: new Date().toISOString(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker assigned to loan successfully");
      setShowAssignModal(false);
      assignForm.resetFields();
      setSelectedTracker(null);
      fetchTrackers();
    } catch (err) {
      console.error("Error assigning GPS tracker:", err);
      message.error(
        "Failed to assign GPS tracker: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveToMaintenance = async (tracker) => {
    try {
      setDeactivatingTracker(tracker.id);

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${tracker.id}`,
        {
          status: "maintenance",
          issued_to: null,
          assigned_loan: null,
          date_issued: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker moved to maintenance successfully");
      fetchTrackers();
    } catch (err) {
      console.error("Error moving GPS tracker to maintenance:", err);
      message.error(
        "Failed to move GPS tracker to maintenance: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeactivatingTracker(null);
    }
  };

  const handleReactivateTracker = async (tracker) => {
    try {
      setDeactivatingTracker(tracker.id);

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${tracker.id}`,
        {
          status: "not_assigned",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker reactivated successfully");
      fetchTrackers();
    } catch (err) {
      console.error("Error reactivating GPS tracker:", err);
      message.error(
        "Failed to reactivate GPS tracker: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeactivatingTracker(null);
    }
  };

  const handleUnassignTracker = async (tracker) => {
    try {
      setDeactivatingTracker(tracker.id);

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${tracker.id}`,
        {
          status: "not_assigned",
          issued_to: null,
          assigned_loan: null,
          date_issued: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker unassigned successfully");
      fetchTrackers();
    } catch (err) {
      console.error("Error unassigning GPS tracker:", err);
      message.error(
        "Failed to unassign GPS tracker: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeactivatingTracker(null);
    }
  };

  const handleDeleteTracker = async (tracker) => {
    try {
      setDeletingTracker(tracker.id);

      await axios.delete(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${tracker.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker deleted successfully");
      fetchTrackers();
    } catch (err) {
      console.error("Error deleting GPS tracker:", err);
      message.error(
        "Failed to delete GPS tracker: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeletingTracker(null);
    }
  };

  const handleMarkAsSold = async (tracker) => {
    try {
      setDeactivatingTracker(tracker.id);

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/gps_trackers/${tracker.id}`,
        {
          status: "sold",
          date_sold: new Date().toISOString(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("GPS tracker marked as sold successfully");
      fetchTrackers();
    } catch (err) {
      console.error("Error marking GPS tracker as sold:", err);
      message.error(
        "Failed to mark GPS tracker as sold: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeactivatingTracker(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      message.error("Please select a CSV file first");
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadProgress(10);

    try {
      // Read and parse the CSV file
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(bulkUploadFile, {
          header: true,
          complete: resolve,
          error: reject,
          skipEmptyLines: true,
        });
      });

      setBulkUploadProgress(30);

      const trackersData = parseResult.data.filter(
        (row) => row.gps_code && row.serial_number && row.sim_number
      );

      if (trackersData.length === 0) {
        throw new Error("No valid GPS tracker data found in the CSV file");
      }

      setBulkUploadProgress(50);

      const successes = [];
      const errors = [];

      // Process each tracker
      for (let i = 0; i < trackersData.length; i++) {
        try {
          const tracker = trackersData[i];

          // Check for duplicates in existing trackers
          const existingSerial = trackers.find(
            (t) => t.serial_number === tracker.serial_number.trim()
          );
          const existingSim = trackers.find(
            (t) => t.sim_number === tracker.sim_number.trim()
          );

          if (existingSerial) {
            errors.push({
              tracker,
              error: `Serial number ${tracker.serial_number} already exists`,
            });
            continue;
          }

          if (existingSim) {
            errors.push({
              tracker,
              error: `SIM number ${tracker.sim_number} already exists`,
            });
            continue;
          }

          // Check for duplicates within the current upload batch
          const duplicateSerialInBatch = trackersData
            .slice(0, i)
            .find(
              (t) => t.serial_number.trim() === tracker.serial_number.trim()
            );
          const duplicateSimInBatch = trackersData
            .slice(0, i)
            .find((t) => t.sim_number.trim() === tracker.sim_number.trim());

          if (duplicateSerialInBatch) {
            errors.push({
              tracker,
              error: `Duplicate serial number ${tracker.serial_number} found in upload file`,
            });
            continue;
          }

          if (duplicateSimInBatch) {
            errors.push({
              tracker,
              error: `Duplicate SIM number ${tracker.sim_number} found in upload file`,
            });
            continue;
          }

          const payload = {
            gps_code: tracker.gps_code.trim(),
            serial_number: tracker.serial_number.trim(),
            sim_number: tracker.sim_number.trim(),
            gps_fee: parseFloat(tracker.gps_fee) || 400, // Default to 400 if not provided
            company: company.id,
            status: "not_assigned", // Default status as requested
            date_created: new Date().toISOString(),
          };

          await axios.post(
            "https://zedlenders.pickmesms.com/items/gps_trackers",
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          successes.push(tracker);
        } catch (err) {
          console.error(
            `Error adding tracker ${trackersData[i].gps_code}:`,
            err
          );
          errors.push({
            tracker: trackersData[i],
            error: err.response?.data?.errors?.[0]?.message || "Unknown error",
          });
        }

        // Update progress
        setBulkUploadProgress(50 + ((i + 1) / trackersData.length) * 40);
      }

      setBulkUploadProgress(100);

      if (successes.length > 0) {
        message.success(
          `Bulk upload completed: ${
            successes.length
          } trackers added successfully${
            errors.length > 0 ? `, ${errors.length} failed` : ""
          }`
        );
      }

      if (errors.length > 0) {
        console.error("Failed entries:", errors);
        message.warning(
          `${errors.length} trackers failed to upload. Check console for details.`
        );
      }

      // Refresh the trackers list
      fetchTrackers();

      // Close modal after success
      setTimeout(() => {
        setBulkUploadModalVisible(false);
        setBulkUploadFile(null);
        setBulkUploadProgress(0);
        setBulkUploadLoading(false);
      }, 1500);
    } catch (error) {
      console.error("Error processing bulk upload:", error);
      message.error("Failed to process bulk upload. Please try again.");
      setBulkUploadLoading(false);
      setBulkUploadProgress(0);
    }
  };

  useEffect(() => {
    fetchTrackers();
    fetchLoans();
  }, [token, company.id]);

  // Filter trackers based on search text
  const filteredTrackers = trackers.filter((tracker) => {
    if (!searchText) return true;

    const searchLower = searchText.toLowerCase();
    return (
      tracker.gps_code?.toLowerCase().includes(searchLower) ||
      tracker.serial_number?.toLowerCase().includes(searchLower) ||
      tracker.sim_number?.toLowerCase().includes(searchLower) ||
      tracker.assigned_loan?.borrower?.first_name
        ?.toLowerCase()
        .includes(searchLower) ||
      tracker.assigned_loan?.borrower?.last_name
        ?.toLowerCase()
        .includes(searchLower) ||
      tracker.status?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate summary statistics based on filtered data
  const stats = {
    total: filteredTrackers.length,
    active: filteredTrackers.filter((tracker) => tracker.status === "active")
      .length,
    maintenance: filteredTrackers.filter(
      (tracker) => tracker.status === "maintenance"
    ).length,
    inactive: filteredTrackers.filter(
      (tracker) => tracker.status === "not_assigned"
    ).length,
    sold: filteredTrackers.filter((tracker) => tracker.status === "sold")
      .length,
  };

  const columns = [
    {
      title: "GPS Code",
      dataIndex: "gps_code",
      key: "gps_code",
      render: (text) => (
        <span>
          <ScanOutlined /> {text}
        </span>
      ),
      sorter: (a, b) => a.gps_code.localeCompare(b.gps_code),
    },
    {
      title: "Serial Number",
      dataIndex: "serial_number",
      key: "serial_number",
      render: (text) => (
        <span>
          <TagsOutlined /> {text}
        </span>
      ),
      sorter: (a, b) => a.serial_number.localeCompare(b.serial_number),
    },
    {
      title: "SIM Number",
      dataIndex: "sim_number",
      key: "sim_number",
      render: (text) => (
        <span>
          <MobileOutlined /> {text}
        </span>
      ),
      sorter: (a, b) => a.sim_number.localeCompare(b.sim_number),
    },
    {
      title: "Date Issued",
      dataIndex: "date_issued",
      key: "date_issued",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      sorter: (a, b) => new Date(a.date_issued) - new Date(b.date_issued),
    },
    {
      title: "Monthly Fee (ZMW)",
      dataIndex: "gps_fee",
      key: "gps_fee",
      render: (fee) => (
        <Tag color="green">K{(fee || 400).toLocaleString()}</Tag>
      ),
      sorter: (a, b) => (a.gps_fee || 400) - (b.gps_fee || 400),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          active: { color: "green", label: "Active" },
          maintenance: { color: "orange", label: "Maintenance" },
          not_assigned: { color: "gray", label: "Not Assigned" },
          sold: { color: "red", label: "Sold" },
        };
        const config = statusConfig[status] || {
          color: "gray",
          label: "Unknown",
        };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: [
        { text: "Active", value: "active" },
        { text: "Maintenance", value: "maintenance" },
        { text: "Not Assigned", value: "not_assigned" },
        { text: "Sold", value: "sold" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Assigned Loan",
      dataIndex: "assigned_loan",
      key: "assigned_loan",
      render: (assigned_loan) =>
        assigned_loan ? (
          <Tag
            color="purple"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/loan/${assigned_loan.id}`)}
          >
            Loan #{assigned_loan.id}
          </Tag>
        ) : (
          <Text type="secondary">No loan assigned</Text>
        ),
    },
    {
      title: "Issued To",
      dataIndex: "assigned_loan",
      key: "issued_to",
      render: (assigned_loan) =>
        assigned_loan?.borrower ? (
          <Tag color="blue">
            {assigned_loan.borrower.first_name +
              " " +
              assigned_loan.borrower.last_name}
          </Tag>
        ) : (
          <Text type="secondary">Not assigned</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => {
        // No actions available for sold trackers
        if (record.status === "sold") {
          return <Text type="secondary">No actions available</Text>;
        }

        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {record.status === "active" && (
              <Popconfirm
                title="Unassign GPS Tracker"
                description="This will unassign the tracker from the loan and make it available for reassignment. Are you sure?"
                onConfirm={() => handleUnassignTracker(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="default"
                  size="small"
                  icon={<DisconnectOutlined />}
                  loading={deactivatingTracker === record.id}
                  block
                >
                  Unassign
                </Button>
              </Popconfirm>
            )}
            {(record.status === "active" ||
              record.status === "not_assigned") && (
              <Popconfirm
                title="Move to Maintenance"
                description="This will move the tracker to maintenance status. Are you sure?"
                onConfirm={() => handleMoveToMaintenance(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="default"
                  size="small"
                  icon={<ToolOutlined />}
                  loading={deactivatingTracker === record.id}
                  block
                >
                  Maintenance
                </Button>
              </Popconfirm>
            )}
            {record.status === "maintenance" && (
              <Popconfirm
                title="Reactivate GPS Tracker"
                description="This will reactivate the tracker and make it available for assignment. Are you sure?"
                onConfirm={() => handleReactivateTracker(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  loading={deactivatingTracker === record.id}
                  block
                >
                  Reactivate
                </Button>
              </Popconfirm>
            )}
            {record.status === "not_assigned" && (
              <>
                <Popconfirm
                  title="Mark as Sold"
                  description="This will mark the tracker as sold and no further actions can be performed. Are you sure?"
                  onConfirm={() => handleMarkAsSold(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    size="small"
                    icon={<TagsOutlined />}
                    loading={deactivatingTracker === record.id}
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                    }}
                    block
                  >
                    Mark as Sold
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="Delete GPS Tracker"
                  description="This will permanently delete the tracker. Are you sure?"
                  onConfirm={() => handleDeleteTracker(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={deletingTracker === record.id}
                    block
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  if (error)
    return (
      <Card>
        <Text type="danger">{error}</Text>
      </Card>
    );

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          GPS Tracker Management
        </Title>
        <Space>
          <Input
            placeholder="Search by GPS code, serial, SIM, borrower name, or status..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 350 }}
            allowClear
          />
          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={() => setBulkUploadModalVisible(true)}
          >
            Bulk Upload
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
          >
            Add New Tracker
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* Summary Statistics Row */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={5}>
            <Card bordered={false}>
              <Statistic
                title="Total Trackers"
                value={stats.total}
                prefix={<SafetyCertificateOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card bordered={false}>
              <Statistic
                title="Active Trackers"
                value={stats.active}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card bordered={false}>
              <Statistic
                title="In Maintenance"
                value={stats.maintenance}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: "#fa8c16" }}
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card bordered={false}>
              <Statistic
                title="Not Assigned"
                value={stats.inactive}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: "#8c8c8c" }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card bordered={false}>
              <Statistic
                title="Sold"
                value={stats.sold}
                prefix={<TagsOutlined />}
                valueStyle={{ color: "#f5222d" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Data Table */}
        <Card
          title="GPS Tracker Details"
          bordered={false}
          headStyle={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <Table
            columns={columns}
            dataSource={filteredTrackers}
            rowKey="id"
            pagination={{
              ...pagination,
              total: filteredTrackers.length,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize,
                }));
              },
            }}
            scroll={{ x: true }}
            bordered
            size="middle"
          />
        </Card>
      </Spin>

      {/* Add New Tracker Modal */}
      <Modal
        title="Add New GPS Tracker"
        open={showAddModal}
        onCancel={() => {
          setShowAddModal(false);
          addForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddTracker}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="GPS Code"
                name="gps_code"
                rules={[{ required: true, message: "Please enter GPS code" }]}
              >
                <Input placeholder="358511098765432" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Serial Number"
                name="serial_number"
                rules={[
                  { required: true, message: "Please enter serial number" },
                ]}
              >
                <Input placeholder="GF0702024056789" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="SIM Number"
                name="sim_number"
                rules={[{ required: true, message: "Please enter SIM number" }]}
              >
                <Input placeholder="+260971234567" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="GPS Monthly Fee (ZMW)"
                name="gps_fee"
                rules={[
                  { required: true, message: "Please enter GPS fee" },
                  {
                    type: "number",
                    min: 0,
                    message: "Fee must be greater than 0",
                  },
                ]}
                initialValue={400}
              >
                <InputNumber
                  placeholder="400"
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `K ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/K\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Add Tracker
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign to Loan Modal */}
      <Modal
        title={`Assign GPS Tracker ${selectedTracker?.gps_code} to Loan`}
        open={showAssignModal}
        onCancel={() => {
          setShowAssignModal(false);
          assignForm.resetFields();
          setSelectedTracker(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignToLoan}>
          <Form.Item
            label="Select Loan"
            name="loan_id"
            rules={[{ required: true, message: "Please select a loan" }]}
          >
            <Select
              placeholder="Select a loan to assign tracker to"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option.children.toString();
                return text.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {loans.map((loan) => (
                <Select.Option key={loan.id} value={loan.id}>
                  {loan.borrower?.first_name} {loan.borrower?.last_name} - K
                  {loan.amount?.toLocaleString()} (Loan #{loan.id})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setShowAssignModal(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Assign Tracker
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title="Bulk Upload GPS Trackers"
        open={bulkUploadModalVisible}
        onCancel={() => {
          setBulkUploadModalVisible(false);
          setBulkUploadFile(null);
          setBulkUploadProgress(0);
        }}
        footer={[
          <Button
            key="download"
            type="danger"
            icon={<DownloadOutlined />}
            onClick={() => {
              const link = document.createElement("a");
              link.href = "/sample_gps_trackers_upload.csv";
              link.download = "sample_gps_trackers_upload.csv";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download Sample
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setBulkUploadModalVisible(false);
              setBulkUploadFile(null);
              setBulkUploadProgress(0);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<UploadOutlined />}
            loading={bulkUploadLoading}
            disabled={!bulkUploadFile || bulkUploadLoading}
            onClick={handleBulkUpload}
          >
            Upload Trackers
          </Button>,
        ]}
        width={700}
      >
        <Alert
          message="Bulk Upload Information"
          description="Upload a CSV file with GPS tracker information. All trackers will be created with 'Not Assigned' status."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Divider orientation="left">Upload CSV File</Divider>
        <p>
          Upload a CSV file with GPS tracker data. The file should have the
          following columns:
        </p>
        <pre style={{ background: "#f5f5f5", padding: 16, borderRadius: 4 }}>
          gps_code,serial_number,sim_number,gps_fee
        </pre>
        <p>
          <strong>Required fields:</strong> gps_code, serial_number, sim_number
        </p>
        <p>
          <strong>Optional fields:</strong> gps_fee (defaults to 400 if not
          provided)
        </p>
        <p>
          <strong>Default values:</strong> status = "not_assigned", company =
          current company, gps_fee = 400
        </p>

        <Upload
          accept=".csv"
          beforeUpload={(file) => {
            const isCsv =
              file.type === "text/csv" || file.name.endsWith(".csv");
            if (!isCsv) {
              message.error("You can only upload CSV files!");
              return Upload.LIST_IGNORE;
            }
            setBulkUploadFile(file);
            return false;
          }}
          onRemove={() => {
            setBulkUploadFile(null);
          }}
          fileList={bulkUploadFile ? [bulkUploadFile] : []}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Select CSV File</Button>
        </Upload>

        {bulkUploadLoading && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={bulkUploadProgress} status="active" />
            <p style={{ textAlign: "center" }}>Uploading GPS trackers...</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GpsTrackers;
